<template>
  <v-container class="pa-6">
    <v-row align="center" style="margin-top: -12px">
      <v-col cols="auto" class="pr-0">
        <div class="text-h5">
          <span
            style="
              font-family: Inter;
              text-transform: uppercase;
              font-weight: 600;
            "
          >
            JSON Status
          </span>
        </div>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="auto">
        <v-chip
          v-if="okCount > 0"
          light
          label
          outlined
          class="rounded-pill"
          style="
            height: 36px;
            background-color: hsl(120, 67%, 94%) !important;
            color: hsl(120, 67%, 39%) !important;
            border-color: hsl(120, 67%, 39%) !important;
          "
        >
          <v-icon color="hsl(120, 67%, 39%)" class="mr-1">mdi-circle</v-icon>
          <span style="font-family: Inter; color: hsl(120, 67%, 39%)">
            {{ okCount }} OK
          </span>
        </v-chip>
        <v-chip
          v-if="warningCount > 0"
          light
          label
          outlined
          class="rounded-pill ml-3"
          style="
            height: 36px;
            background-color: hsl(40, 87%, 94%) !important;
            color: hsl(40, 87%, 49%) !important;
            border-color: hsl(40, 87%, 49%) !important;
          "
        >
          <v-icon color="hsl(40, 87%, 49%)" class="mr-1">mdi-circle</v-icon>
          <span style="font-family: Inter; color: hsl(40, 87%, 49%)">
            {{ warningCount }} Warning
          </span>
        </v-chip>
        <v-chip
          v-if="errorCount > 0"
          light
          label
          outlined
          class="rounded-pill ml-3"
          style="
            height: 36px;
            background-color: hsl(0, 87%, 94%) !important;
            color: hsl(0, 87%, 59%) !important;
            border-color: hsl(0, 87%, 59%) !important;
          "
        >
          <v-icon color="hsl(0, 87%, 59%)" class="mr-1">mdi-circle</v-icon>
          <span style="font-family: Inter; color: hsl(0, 87%, 59%)">
            {{ errorCount }} Error
          </span>
        </v-chip>
        <v-chip
          v-if="unknownCount > 0"
          light
          label
          outlined
          class="rounded-pill ml-3"
          style="
            height: 36px;
            background-color: hsl(0, 0%, 94%) !important;
            color: hsl(0, 0%, 59%) !important;
            border-color: hsl(0, 0%, 59%) !important;
          "
        >
          <v-icon color="hsl(0, 0%, 59%)" class="mr-1">mdi-circle</v-icon>
          <span style="font-family: Inter; color: hsl(0, 0%, 59%)">
            {{ unknownCount }} Unknown
          </span>
        </v-chip>
      </v-col>
      <v-col cols="auto" class="pa-0 pr-3 ml-3">
        <v-switch
          v-model="alertingEnabled"
          :loading="isUpdatingAlerts"
          label="Alerting"
          inset
          dense
          @change="toggleAlerting"
        ></v-switch>
      </v-col>
    </v-row>
    <v-row class="mt-0">
      <v-col cols="12" class="pt-0">
        <chains-json-table class="mt-6" :chains="monitoredChains" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import ChainsJsonTable from '../components/Chains.json/ChainsJsonTable.vue';
import { mapGetters } from 'vuex';
import { formatChainMetricValue } from '@/services/chainMetricService';
import axios from 'axios';

export default {
  components: {
    ChainsJsonTable,
  },
  data() {
    return {
      isUpdatingAlerts: false,
      alertingEnabled: false,
    };
  },
  computed: {
    ...mapGetters(['getChainMetric']),
    monitoredChains() {
      return this.$store.state.chain.chains.filter(
        (chain) => chain.monitor_bp_json
      );
    },
    okCount() {
      return this.monitoredChains.filter(
        (chain) => this.getChainStatusFormatted(chain) === 'OK'
      ).length;
    },
    warningCount() {
      return this.monitoredChains.filter(
        (chain) => this.getChainStatusFormatted(chain) === 'Warning'
      ).length;
    },
    errorCount() {
      return this.monitoredChains.filter(
        (chain) => this.getChainStatusFormatted(chain) === 'Error'
      ).length;
    },
    unknownCount() {
      return this.monitoredChains.filter(
        (chain) => this.getChainStatusFormatted(chain) === 'Unknown'
      ).length;
    },
  },
  watch: {
    // Watch monitoredChains and load alert state when it changes
    monitoredChains: {
      immediate: true, // This makes it run on component creation
      handler(newChains) {
        if (newChains && newChains.length > 0) {
          this.loadAlertingState();
        }
      },
    },
  },
  mounted() {
    // We can keep this as a backup, but the watcher should handle most cases
    this.loadAlertingState();
  },
  methods: {
    getChainStatus(chain) {
      return this.getChainMetric(chain.id, 'Chain/BpJsonStatus') || 'Unknown';
    },
    getChainStatusFormatted(chain) {
      return formatChainMetricValue(
        'Chain/BpJsonStatus',
        this.getChainStatus(chain)
      ).short;
    },
    async loadAlertingState() {
      try {
        if (this.monitoredChains && this.monitoredChains.length > 0) {
          try {
            // Check all monitored chains
            const results = await Promise.all(
              this.monitoredChains.map((chain) =>
                axios.get(`/api/v1/chainalerts/${chain.id}/BpJsonStatus`)
              )
            );

            // All chains must have alerts with condition 'on' for alerting to be enabled
            this.alertingEnabled = results.every(
              (response) => response.data?.condition === 'on'
            );
          } catch (error) {
            // If we get any errors, alerting is considered disabled
            this.alertingEnabled = false;
            if (error.response?.status !== 404) {
              // Only log non-404 errors
              console.error('Error loading alerting state:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error loading alerting state:', error);
      }
    },
    async toggleAlerting(enabled) {
      this.isUpdatingAlerts = true;
      try {
        // Build array of all monitored chains
        const alerts = this.monitoredChains.map((chain) => {
          // Find existing chain-level BpJsonStatus alert
          const existingAlert = chain.alerts?.find(
            (a) =>
              a.source_id === chain.id &&
              a.source_type === 'chain' &&
              a.metric === 'Chain/BpJsonStatus'
          );

          // Reuse existing alert id if present, else generate a new ID
          return {
            id: existingAlert ? existingAlert.id : crypto.randomUUID(),
            source_id: chain.id,
            source_type: 'chain',
            metric: 'Chain/BpJsonStatus',
            condition: enabled ? 'on' : '',
            url: '',
          };
        });

        // Bulk update alerts
        await axios.post('/api/v1/chainalerts/bulk', { alerts });

        // Confirm in the UI by retrieving them again
        await this.loadAlertingState();
      } catch (error) {
        console.error('Error updating alerts:', error);
        this.alertingEnabled = !enabled; // revert if there's an error
      } finally {
        this.isUpdatingAlerts = false;
      }
    },
  },
};
</script>

<style></style>
